import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { MessageSquare, Send, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { discussionsAPI, coursesAPI, enrollmentsAPI } from '../lib/api';
import { toast } from 'sonner';

export default function ForumsPage() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [discussions, setDiscussions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(courseId || '');
  const [showForm, setShowForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newReply, setNewReply] = useState({});

  useEffect(() => { fetchCourses(); }, []);
  useEffect(() => { if (selectedCourse) fetchDiscussions(); else { setDiscussions([]); setLoading(false); } }, [selectedCourse]);

  const fetchCourses = async () => {
    try {
      const enrollmentsRes = await enrollmentsAPI.getMy();
      const coursePromises = enrollmentsRes.data.map(e => coursesAPI.getById(e.course_id));
      const courseResults = await Promise.all(coursePromises);
      setCourses(courseResults.map(r => r.data));
      if (courseId) setSelectedCourse(courseId);
    } catch (error) { toast.error('Failed to load courses'); }
  };

  const fetchDiscussions = async () => {
    setLoading(true);
    try {
      const response = await discussionsAPI.getByCourse(selectedCourse);
      setDiscussions(response.data);
    } catch (error) { toast.error('Failed to load discussions'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newTitle || !newContent) { toast.error('Fill all fields'); return; }
    try {
      await discussionsAPI.create({ course_id: selectedCourse, title: newTitle, content: newContent });
      toast.success('Created!');
      setNewTitle(''); setNewContent(''); setShowForm(false);
      fetchDiscussions();
    } catch (error) { toast.error('Failed'); }
  };

  const handleReply = async (discussionId) => {
    const content = newReply[discussionId];
    if (!content) { toast.error('Enter reply'); return; }
    try {
      await discussionsAPI.addReply(discussionId, content);
      toast.success('Reply added!');
      setNewReply({ ...newReply, [discussionId]: '' });
      fetchDiscussions();
    } catch (error) { toast.error('Failed'); }
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-sketch text-4xl text-ink">Forums</h1>
            <p className="text-muted-foreground mt-1">Connect with learners</p>
          </div>
          {selectedCourse && <Button className="btn-sketchy" onClick={() => setShowForm(!showForm)}><Plus className="w-4 h-4 mr-2" /> New</Button>}
        </div>

        <div className="card-sketchy">
          <Label className="form-label">Select Course</Label>
          <Select value={selectedCourse} onValueChange={setSelectedCourse}>
            <SelectTrigger className="input-sketchy"><SelectValue placeholder="Choose course" /></SelectTrigger>
            <SelectContent>{courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {showForm && (
          <div className="card-sketchy space-y-4">
            <Label>Title</Label>
            <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} className="input-sketchy" placeholder="Question" />
            <Label>Content</Label>
            <Textarea value={newContent} onChange={e => setNewContent(e.target.value)} className="input-sketchy" placeholder="Details" />
            <Button className="btn-sketchy" onClick={handleCreate}>Post</Button>
          </div>
        )}

        {loading ? <div className="flex justify-center py-8"><div className="spinner" /></div> : selectedCourse && discussions.length > 0 ? (
          <div className="space-y-6">
            {discussions.map(d => (
              <div key={d.id} className="card-sketchy">
                <h3 className="font-bold text-lg">{d.title}</h3>
                <p className="text-muted-foreground mt-1">{d.content}</p>
                <div className="text-xs text-muted-foreground mt-2">{d.user_name} • {new Date(d.created_at).toLocaleDateString()}</div>
                {d.replies?.length > 0 && <div className="mt-4 space-y-2">{d.replies.map(r => <div key={r.id} className="p-3 bg-gray-50 border-l-4 border-primary rounded-r ml-8"><span className="font-semibold">{r.user_name}:</span> {r.content}</div>)}</div>}
                <div className="flex gap-2 mt-4 pt-4 border-t border-dashed">
                  <Input value={newReply[d.id] || ''} onChange={e => setNewReply({ ...newReply, [d.id]: e.target.value })} className="input-sketchy flex-1" placeholder="Reply..." />
                  <Button onClick={() => handleReply(d.id)} className="btn-sketchy"><Send className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        ) : selectedCourse ? (
          <div className="card-sketchy text-center py-16">
            <MessageSquare className="w-20 h-20 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-sketch text-2xl mb-2">No discussions</h3>
            <p className="text-muted-foreground">Start one!</p>
          </div>
        ) : (
          <div className="card-sketchy text-center py-16">
            <MessageSquare className="w-20 h-20 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-sketch text-2xl">Select a course</h3>
          </div>
        )}
      </div>
    </Layout>
  );
}
